'use client';

import React, { useState } from 'react';
import { Bookmark, Share2, ListPlus, Play, Pause } from 'lucide-react';
import type { PublicArticle } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useAudio, type QueueTrack } from '@/lib/contexts/ListenQueueContext.shim';

/**
 * ArticleHeroActions — action cluster on the article hero row.
 *
 * Spec: design-references/article.html `.a-hero__actions`.
 * Buttons: Save · Listen now (primary, pulses when playing) · Add to queue · Share.
 *
 * Share uses Web Share API when available, falls back to clipboard + toast.
 */

const BOOKMARKS_KEY = 'aviado_bookmarks_v1';

export interface ArticleHeroActionsProps {
  article: PublicArticle;
  className?: string;
}

export function ArticleHeroActions({ article, className }: ArticleHeroActionsProps) {
  const { currentTrack, isPlaying, addToQueue, playTrack, isInQueue, togglePlayPause } =
    useAudio();
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = React.useRef<number | null>(null);

  // Hydrate bookmark state
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(BOOKMARKS_KEY);
      const ids = raw ? JSON.parse(raw) : [];
      if (Array.isArray(ids)) setSaved(ids.includes(article.id));
    } catch {}
  }, [article.id]);

  const showToast = (msg: string) => {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast(msg);
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 1800);
  };

  // Clean up pending timer on unmount
  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const toggleSave = () => {
    try {
      const raw = localStorage.getItem(BOOKMARKS_KEY);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      const hasIt = Array.isArray(ids) && ids.includes(article.id);
      const next = hasIt
        ? ids.filter((x) => x !== article.id)
        : [...(Array.isArray(ids) ? ids : []), article.id];
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(next));
      setSaved(next.includes(article.id));
      showToast(next.includes(article.id) ? 'Saved' : 'Removed');
    } catch {}
  };

  const track: QueueTrack | null = article.audio_url
    ? {
        id: article.id,
        slug: article.slug,
        title: article.title,
        supplement: article.supplement,
        audioUrl: article.audio_url,
        evidenceTier: article.evidence_tier,
        durationSeconds: article.audio_duration_seconds,
      }
    : null;

  const isThisPlaying = currentTrack?.id === article.id && isPlaying;
  const inQueueAlready = track ? isInQueue(track.id) : false;

  const onListenNow = () => {
    if (!track) return;
    if (currentTrack?.id === article.id) togglePlayPause();
    else playTrack(track);
  };

  const onAddQueue = () => {
    if (!track) return;
    addToQueue(track);
    showToast(inQueueAlready ? 'Already queued' : 'Added to queue');
  };

  const onShare = async () => {
    if (typeof window === 'undefined') return;
    const url = window.location.href;
    const title = article.title;
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    try {
      if (typeof nav.share === 'function') {
        await nav.share({ title, url });
        return;
      }
      if (nav.clipboard) {
        await nav.clipboard.writeText(url);
        showToast('Link copied');
        return;
      }
      // Insecure origin / no clipboard / no Web Share → surface the state
      showToast('Copy not supported in this browser');
    } catch {
      // User cancelled share or clipboard unavailable
    }
  };

  const runtime = formatRuntime(article.audio_duration_seconds);

  return (
    <div
      className={cn('flex gap-2 flex-wrap', className)}
      role="group"
      aria-label="Article actions"
    >
      {/* Save (ghost) */}
      <HeroGhostButton
        onClick={toggleSave}
        aria-pressed={saved}
        aria-label={saved ? 'Remove bookmark' : 'Bookmark article'}
        active={saved}
        className="max-lg:order-1 max-lg:flex-1 max-lg:justify-center"
      >
        <Bookmark className="w-[14px] h-[14px]" strokeWidth={2} fill={saved ? 'currentColor' : 'none'} />
        <span>{saved ? 'Saved' : 'Save'}</span>
      </HeroGhostButton>

      {/* Listen now (primary) */}
      {track && (
        <button
          type="button"
          onClick={onListenNow}
          aria-pressed={isThisPlaying}
          aria-label={isThisPlaying ? 'Pause' : 'Listen to this review'}
          className={cn(
            'inline-flex items-center gap-2',
            'px-3.5 py-[9px] pl-[10px] rounded-md',
            'bg-warm-navy text-white border border-warm-navy',
            'font-ui text-[12px] font-medium',
            'hover:bg-aviado-blue hover:border-aviado-blue',
            'transition-colors duration-fast',
            'max-lg:order-first max-lg:basis-full max-lg:justify-center max-lg:py-3'
          )}
        >
          <span
            className={cn(
              'inline-flex items-center justify-center w-[22px] h-[22px] text-aviado-teal',
              'transition-transform duration-fast',
              isThisPlaying && 'animate-pulse-soft'
            )}
            aria-hidden="true"
          >
            {isThisPlaying ? (
              <Pause className="w-[18px] h-[18px]" fill="currentColor" />
            ) : (
              <Play className="w-[18px] h-[18px]" fill="currentColor" />
            )}
          </span>
          <span>{isThisPlaying ? 'Pause' : 'Listen now'}</span>
          {runtime && (
            <span className="pl-2 ml-0.5 border-l border-white/[0.18] font-mono text-[10.5px] tracking-[0.06em] text-white/[0.55]">
              {runtime}
            </span>
          )}
        </button>
      )}

      {/* Add to queue (ghost) */}
      {track && (
        <HeroGhostButton
          onClick={onAddQueue}
          aria-pressed={inQueueAlready}
          aria-label={inQueueAlready ? 'Already in queue' : 'Add to listen queue'}
          active={inQueueAlready}
          className="max-lg:order-1 max-lg:flex-1 max-lg:justify-center"
        >
          <ListPlus className="w-[14px] h-[14px]" strokeWidth={2} />
          <span>{inQueueAlready ? 'Queued' : 'Add to queue'}</span>
        </HeroGhostButton>
      )}

      {/* Share (ghost) */}
      <HeroGhostButton onClick={onShare} aria-label="Share" className="max-lg:order-1 max-lg:flex-1 max-lg:justify-center">
        <Share2 className="w-[14px] h-[14px]" strokeWidth={2} />
        <span>Share</span>
      </HeroGhostButton>

      {/* Toast */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'fixed bottom-[100px] left-1/2 -translate-x-1/2 z-[90]',
            'bg-warm-navy text-white px-4 py-2.5 rounded-md',
            'font-mono text-[11.5px] tracking-[0.04em]',
            'shadow-lg animate-fade-in'
          )}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function HeroGhostButton({
  children,
  active,
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        'inline-flex items-center gap-1.5',
        'px-3 py-2 rounded-md',
        'bg-white border border-border',
        'font-ui text-[12px] font-medium',
        'hover:border-border-strong hover:bg-neutral-25',
        'transition-all duration-fast',
        active && 'text-aviado-teal-text border-aviado-teal',
        className
      )}
    >
      {children}
    </button>
  );
}

function formatRuntime(seconds?: number | null): string | null {
  if (!seconds || !Number.isFinite(seconds)) return null;
  return `${Math.max(1, Math.ceil(seconds / 60))} min`;
}

export default ArticleHeroActions;
